import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_Accounting_FindInvoicesTable_VM_Row } from 'ab-api/web/accounting/findInvoicesTable';

import type InoviceReminderRecipientsVM from 'ab-viewModels/workRequest/invoiceReminderRecipients';
import type UserGroupAndPmVM from 'ab-viewModels/workRequest/userGroupAndPmVM.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { InvoiceBillingLookupType } from '@acceligentllc/shared/enums/invoiceBillingLookupType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import type { RootState } from 'af-reducers';

import { bemElement } from 'af-utils/bem.util';
import * as SettingsUtils from 'af-utils/settings.util';
import * as UserUtils from 'af-utils/user.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import InvoiceStatusDisplay from 'ab-enums/invoiceStatusDisplay.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import * as SettingsKeys from 'af-constants/settingsKeys';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { errorToast, loadingToast, successToast } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ConfirmationModal from 'af-components/ConfirmationModal';
import Dropdown from 'af-components/Controls/Dropdown';
import DateFilter from 'af-components/DateFilter';
import RectangleButton from 'af-components/MultipleOptionsButton/RectangleButton';
import type { TableRef } from 'af-components/Table';
import TableNew from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import DollarCell from 'af-components/Table/Cells/DollarCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import PercentageCell from 'af-components/Table/Cells/PercentageCell';
import TextCell from 'af-components/Table/Cells/TextCell';
import type { TableProps } from 'af-components/Table/types';

import CLIENT from 'af-routes/client';

import * as accountingActions from 'af-actions/accounting';
import * as AttachmentActions from 'af-actions/attachment';
import * as jobActions from 'af-actions/jobs';

import InvoiceJobSelectModal from './InvoiceJobSelectModal';
import { InvoiceStatusFilterItem } from '../../Jobs/Preview/Invoices/InvoiceStatusFilterItem';
import styles from '../../Jobs/Preview/Invoices/styles.module.scss';

interface SettingProps {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
	storageStatusId: number;
}

type Props = ConnectedProps<typeof connector> & SettingProps & CustomRouteComponentProps;

const setLocalStorageStartDate = (date: Date = new Date()) => {
	SettingsUtils.setItemWithFormatter(
		SettingsKeys.ACCOUNTING_INVOICES_START_DATE(),
		date,
		(value: Date) => TimeUtils.formatDate(value, TimeFormat.FULL_DATE),
		BrowserStorageEnum.LOCAL_STORAGE
	);
};

const setLocalStorageEndDate = (date: Date = new Date()) => {
	SettingsUtils.setItemWithFormatter(
		SettingsKeys.ACCOUNTING_INVOICES_END_DATE(),
		date,
		(value: Date) => TimeUtils.formatDate(value, TimeFormat.FULL_DATE),
		BrowserStorageEnum.LOCAL_STORAGE
	);
};

function setInvoicesStatus(option: InvoiceStatusFilterItem): void {
	SettingsUtils.setItem(SettingsKeys.ACCOUNTING_INVOICES_STATUS(), `${option.id}`, BrowserStorageEnum.LOCAL_STORAGE);
}

const setLocalStoragePeriod = (period: TimePeriodRecurrence) => {
	SettingsUtils.setItem(SettingsKeys.ACCOUNTING_INVOICES_PERIOD(), period, BrowserStorageEnum.LOCAL_STORAGE);
};

const normalizeDateToDate = (item: string) => {
	return TimeUtils.normalizeDateToDate(item, TimeFormat.FULL_DATE);
};

const BREADCRUMBS = [{ label: 'Invoices' }];

const Invoices: React.FC<Props> = (props) => {
	const {
		getInvoicesTable,
		invoiceJobInvoice,
		deleteInvoice,
		sendManualReminderForInvoice,
		downloadAttachmentsAsZip,
		getUserGroupAndPM,
		getInvoiceReminderRecipients,
		companyName,
		isSendingInvoiceNotificationsEnabled,
		location: { state: { orgAlias } },
		storageStatusId,
		isAdmin,
	} = props;

	const tableRef = React.useRef<TableRef<W_Accounting_FindInvoicesTable_VM_Row>>(null);
	const [startDate, setStartDate] = React.useState(TimeUtils.positionDate(props.startDate, 'start', 'day'));
	const [endDate, setEndDate] = React.useState(TimeUtils.positionDate(props.endDate, 'end', 'day'));
	const [period, setPeriod] = React.useState(props.period);
	const [currentlyActiveRow, setCurrentlyActiveRow] = React.useState<Nullable<W_Accounting_FindInvoicesTable_VM_Row>>(null);
	const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
	const [showInvoiceModal, setShowInvoiceModal] = React.useState<boolean>(false);
	const [showSendReminderModal, setShowSendReminderModal] = React.useState<boolean>(false);
	const [showInvoiceJobSelectModal, setShowInvoiceJobSelectModal] = React.useState<boolean>(false);
	const [filterByStatus, setFilterByStatus] = React.useState<Nullable<InvoiceStatusDisplay>>(
		isAdmin
			? null
			: (InvoiceStatusFilterItem.STATUSES.find((status) => status.id === +storageStatusId)?.name ?? null) as Nullable<InvoiceStatusDisplay>
	);
	const [userGroupAndPM, setUserGroupAndPM] = React.useState<Nullable<UserGroupAndPmVM>>(null);
	const [additionalRecipients, setAdditionalRecipients] = React.useState<Nullable<InoviceReminderRecipientsVM[]>>(null);

	const DELETE_INVOICE_MODAL_BODY = (
		<>
			You are about to delete this invoice.
			<br />
			This action can not be reversed.
			<br />
			Are you sure you want to proceed?
		</>
	);

	const INVOICE_INVOICE_MODAL_BODY = React.useMemo(() => (
		<>
			You are about to change the status of this draft to <i>Invoiced</i>.
			<br />
			Some sections of the form will no longer be editable.
			<br />
			<br />
			Continue with invoicing?
		</>
	), []);

	const getSendReminderModalBody = React.useCallback(() => {
		if (currentlyActiveRow?.billingContacts?.length) {
			return (<>
				Payment reminder will be sent to following billing contact(s):
				<br />
				{
					currentlyActiveRow.billingContacts.map((_billingContact, index) => {
						let contactValue = '';
						let contactEmails: Nullable<string> = null;
						if (_billingContact.type === InvoiceBillingLookupType.EMAIL_ONLY) {
							contactValue = _billingContact.email;
						} else if (_billingContact.type === InvoiceBillingLookupType.CONTACT) {
							contactValue = _billingContact.contact.fullName;
							const selectedEmails = new Set(_billingContact.contact.contactEmailIds);
							const selectedEmailsValues = _billingContact.contact.emails.reduce((_emails, _email) => {
								if (selectedEmails.has(_email.id)) {
									_emails.push(_email.value);
								}
								return _emails;
							}, [] as string[]);
							contactEmails = ` (${selectedEmailsValues.join(', ')})`;
						}

						return (
							<React.Fragment key={index}>
								<b>
									{contactValue}
								</b>
								{contactEmails && (
									<span>
										{contactEmails}
									</span>)
								}
								{index !== currentlyActiveRow.billingContacts.length - 1 ? ', ' : '.'}
							</React.Fragment>
						);
					})
				}
				{
					userGroupAndPM?.userGroupName &&
					<>
						< br />
						< br />
						user group: <b>{userGroupAndPM?.userGroupName}</b>
					</>
				}
				{
					userGroupAndPM?.pmFirstName &&
					<>
						< br />
						< br />
						and project manager: <b>{userGroupAndPM?.pmFirstName} {userGroupAndPM?.pmLastName} ({userGroupAndPM?.pmEmail})</b>
					</>
				}
				{
					!!additionalRecipients?.length &&
					<>
						< br />
						< br />
						and recipients: {additionalRecipients.map((_recipient, index) => {
							return <b key={index}>
								{` ${_recipient.firstName} ${_recipient.lastName} ${_recipient.email ? ` (${_recipient.email})` : ''}${additionalRecipients.length - 1 === index ? '' : ','}`}
							</b>;
						})}
					</>
				}
				< br />
				<br />
				Send reminder?
			</>);
		}
	}, [currentlyActiveRow, userGroupAndPM, additionalRecipients]);

	const getInvoiceModalBody = React.useCallback(() => {
		if (currentlyActiveRow?.billingContacts?.length && currentlyActiveRow.sendReminderOnInvoice) {
			return (<>
				You are about to change status of this draft to <i>Invoiced</i>.
				<br />
				You will not be able to make any changes to the data after it has been invoiced.
				<br />
				<br />
				Invoice information will be sent to following billing contacts:
				<br />
				{
					currentlyActiveRow.billingContacts.map((_billingContact, index) => {
						let contactValue = '';
						let contactEmails: Nullable<string> = null;
						if (_billingContact.type === InvoiceBillingLookupType.EMAIL_ONLY) {
							contactValue = _billingContact.email;
						} else if (_billingContact.type === InvoiceBillingLookupType.CONTACT) {
							contactValue = _billingContact.contact.fullName;
							const selectedEmails = new Set(_billingContact.contact.contactEmailIds);
							const selectedEmailsValues = _billingContact.contact.emails.reduce((_emails, _email) => {
								if (selectedEmails.has(_email.id)) {
									_emails.push(_email.value);
								}
								return _emails;
							}, [] as string[]);
							contactEmails = ` (${selectedEmailsValues.join(', ')})`;
						}

						return (
							<React.Fragment key={index}>
								<b>
									{contactValue}
								</b>
								{contactEmails && (
									<span>
										{contactEmails}
									</span>)
								}
								{index !== currentlyActiveRow.billingContacts.length - 1 ? ', ' : '.'}
							</React.Fragment>
						);
					})
				}
				{
					userGroupAndPM?.userGroupName &&
					<>
						< br />
						< br />
						user group: <b>{userGroupAndPM?.userGroupName}</b>
					</>
				}
				{
					userGroupAndPM?.pmFirstName &&
					<>
						< br />
						< br />
						and project manager: <b>{userGroupAndPM?.pmFirstName} {userGroupAndPM?.pmLastName} ({userGroupAndPM?.pmEmail})</b>
					</>
				}
				{
					!!additionalRecipients?.length &&
					<>
						< br />
						< br />
						and recipients: {additionalRecipients.map((_recipient, index) => {
							return <b key={index}>
								{` ${_recipient.firstName} ${_recipient.lastName} ${_recipient.email ? ` (${_recipient.email})` : ''}${additionalRecipients.length - 1 === index ? '' : ','}`}
							</b>;
						})}
					</>
				}
				< br />
				< br />
				To invoice without sending an email, uncheck this option in the
				< i > Reminders</i > section of this invoice.
				< br />
				<br />
				Continue with invoicing ?
			</>);
		}
		return INVOICE_INVOICE_MODAL_BODY;
	}, [INVOICE_INVOICE_MODAL_BODY, currentlyActiveRow, userGroupAndPM, additionalRecipients]);

	const closeDeleteModal = React.useCallback(() => setShowDeleteModal(false), []);
	const closeInvoiceModal = React.useCallback(() => setShowInvoiceModal(false), []);
	const closeSendReminderModal = React.useCallback(() => setShowSendReminderModal(false), []);
	const closeInvoiceJobSelectModal = React.useCallback(() => setShowInvoiceJobSelectModal(false), []);

	const openNewInvoiceModal = React.useCallback(async () => setShowInvoiceJobSelectModal(true), []);

	const history = useHistory();

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery): Promise<TableContent<W_Accounting_FindInvoicesTable_VM_Row>> => {
		const { page, pageSize, filterByText, sortBy } = tableRequestModel;

		const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
		const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);

		const result = await getInvoicesTable(
			new TableQuery({ page, pageSize, sortBy, filterByText }),
			startDateString,
			endDateString,
			filterByStatus
		);

		return result;
	}, [getInvoicesTable, startDate, endDate, filterByStatus]);

	const goToEditInvoice = React.useCallback(async (invoice: W_Accounting_FindInvoicesTable_VM_Row) => {
		if (invoice.id && invoice.workRequestId) {
			history.push({
				pathname: CLIENT.COMPANY.JOBS.INVOICE_EDIT(orgAlias, companyName, invoice.workRequestId.toString(), invoice.id.toString()),
				state: {
					jobCode: invoice.jobCode,
					backToAccountingPage: true,
				},
			});
		} else {
			throw new Error('Missing invoice id or job id');
		}
	}, [companyName, history, orgAlias]);

	const goToPreviewInvoice = React.useCallback(async (invoice: W_Accounting_FindInvoicesTable_VM_Row) => {
		if (invoice.id && invoice.workRequestId) {
			history.push({
				pathname: CLIENT.COMPANY.JOBS.INVOICE_PREVIEW(orgAlias, companyName, invoice.workRequestId.toString(), invoice.id.toString()),
				state: {
					jobCode: invoice.jobCode,
					backToAccountingPage: true,
				},
			});
		} else {
			throw new Error('Missing invoice id or job id');
		}
	}, [companyName, history, orgAlias]);

	const onRowClick = React.useCallback((_row: Row<W_Accounting_FindInvoicesTable_VM_Row>) => {
		setCurrentlyActiveRow(_row.original);
		if (_row.original.status === InvoiceStatusDisplay.DRAFT && _row.original.id) {
			goToEditInvoice(_row.original);
		} else {
			goToPreviewInvoice(_row.original);
		}
	}, [goToEditInvoice, goToPreviewInvoice]);

	const invoiceInvoiceRow = React.useCallback(() => async () => {
		if (!currentlyActiveRow) {
			throw new Error('Currently active row not set.');
		}
		if (!currentlyActiveRow.workRequestId) {
			throw new Error('Currently active row no work request id.');
		}
		if (currentlyActiveRow.status === InvoiceStatusDisplay.DRAFT) {
			await invoiceJobInvoice(currentlyActiveRow.workRequestId, currentlyActiveRow.id);
		}
		tableRef.current?.refreshTable();
		setCurrentlyActiveRow(null);
	}, [currentlyActiveRow, invoiceJobInvoice]);

	const deleteInvoiceRow = React.useCallback(() => async () => {
		if (!currentlyActiveRow) {
			throw new Error('Currently active row not set.');
		}
		if (!currentlyActiveRow.workRequestId) {
			throw new Error('Currently active row no work request id.');
		}
		await deleteInvoice(currentlyActiveRow.workRequestId, currentlyActiveRow.id);
		tableRef.current?.refreshTable();
		setCurrentlyActiveRow(null);
	}, [currentlyActiveRow, deleteInvoice]);

	const onInvoiceRow = React.useCallback((_invoice: W_Accounting_FindInvoicesTable_VM_Row) => async () => {
		const usrGroupPM = await getUserGroupAndPM(_invoice.workRequestId);
		const additionalReminderContacts = await getInvoiceReminderRecipients(_invoice.workRequestId);
		setAdditionalRecipients(additionalReminderContacts);
		setUserGroupAndPM(usrGroupPM);
		setCurrentlyActiveRow(_invoice);
		setShowInvoiceModal(true);
	}, [getUserGroupAndPM, getInvoiceReminderRecipients]);

	const onDeleteRow = React.useCallback((_invoice: W_Accounting_FindInvoicesTable_VM_Row) => async () => {
		setCurrentlyActiveRow(_invoice);
		setShowDeleteModal(true);
	}, []);

	const onEditRow = React.useCallback((row: Row<W_Accounting_FindInvoicesTable_VM_Row>) => () => {
		setCurrentlyActiveRow(row.original);
		onRowClick(row);
	}, [onRowClick]);

	const onSendManualReminder = React.useCallback((invoice: W_Accounting_FindInvoicesTable_VM_Row) => async () => {
		const usrGroupPM = await getUserGroupAndPM(invoice.workRequestId);
		const additionalReminderContacts = await getInvoiceReminderRecipients(invoice.workRequestId);
		setUserGroupAndPM(usrGroupPM);
		setAdditionalRecipients(additionalReminderContacts);
		setCurrentlyActiveRow(invoice);
		setShowSendReminderModal(true);
	}, [getUserGroupAndPM, getInvoiceReminderRecipients]);

	const sendManualReminder = React.useCallback(() => async () => {
		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
		loadingToast(`${currentlyActiveRow?.id}`, { title: 'Sending invoice reminder to billing contacts...', timestamp: startingTimestamp });

		try {
			if (currentlyActiveRow) {
				await sendManualReminderForInvoice(currentlyActiveRow.id);
				successToast(
					`${currentlyActiveRow.id}`,
					{ title: `Reminders for invoice ${currentlyActiveRow.invoiceCode} were sent to the billing contacts.`, timestamp: startingTimestamp }
				);
			}
		} catch (error) {
			if (error.errors.billingContacts) {
				errorToast(
					`${currentlyActiveRow?.id}`,
					{ title: 'Error sending invoices', description: error.errors.billingContacts, timestamp: startingTimestamp }
				);
			}
		}

	}, [currentlyActiveRow, sendManualReminderForInvoice]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<W_Accounting_FindInvoicesTable_VM_Row, unknown>) => {
		const actions = [
			{ onClick: onEditRow(_cell.row), label: 'Edit' },
			{ onClick: onDeleteRow(_cell.row.original), label: 'Delete' },
		];
		if (_cell.row.original.status === InvoiceStatusDisplay.DRAFT) {
			actions.push({ onClick: onInvoiceRow(_cell.row.original), label: 'Invoice' });
		}
		if (_cell.row.original.status !== InvoiceStatusDisplay.DRAFT
			&& _cell.row.original.status !== InvoiceStatusDisplay.PAID
			&& _cell.row.original.status !== InvoiceStatusDisplay.OVERPAID
			&& isSendingInvoiceNotificationsEnabled
			&& _cell.row.original.billingContacts?.length
		) {
			actions.push({ onClick: onSendManualReminder(_cell.row.original), label: 'Send Reminder' });
		}
		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [onDeleteRow, onEditRow, onInvoiceRow, onSendManualReminder, isSendingInvoiceNotificationsEnabled]);

	const changePeriod = React.useCallback((_period: TimePeriodRecurrence, _selected: Date) => {
		let _startDate: Date = _selected, _endDate: Date = _selected;

		switch (_period) {
			case TimePeriodRecurrence.MONTHLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'month'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'month'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.WEEKLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'week'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'week'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.DAILY:
			case TimePeriodRecurrence.CUSTOM:
				_startDate = TimeUtils.positionDate(_selected, 'start', 'day');
				_endDate = TimeUtils.positionDate(_selected, 'end', 'day');
			default:
				break;
		}

		tableRef.current?.resetPagination();
		setStartDate(_startDate);
		setEndDate(_endDate);
		setPeriod(_period);
	}, []);

	const filterByDate = React.useCallback((_startDate: Date, _endDate: Date) => {
		tableRef.current?.resetPagination();
		setStartDate(TimeUtils.positionDate(_startDate, 'start', 'day'));
		setEndDate(TimeUtils.positionDate(_endDate, 'end', 'day'));
	}, []);

	const filterByInvoiceStatus = React.useCallback((value: InvoiceStatusFilterItem) => {
		if (!isAdmin) {
			setInvoicesStatus(value);
		}

		tableRef.current?.resetPagination();
		if (Object.values<string>(InvoiceStatusDisplay).includes(value.name)) {
			setFilterByStatus(value.name as InvoiceStatusDisplay);
		} else {
			setFilterByStatus(null);
		}
	}, [isAdmin]);

	const renderInvoiceStatus = React.useCallback((invoiceStatus: InvoiceStatusDisplay) => {
		const cellStyle: string[] = [];
		cellStyle.push(styles['invoice__status-cell']);

		switch (invoiceStatus) {
			case InvoiceStatusDisplay.DRAFT: {
				cellStyle.push(styles['invoice__status-cell--draft']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-draft" />
						<b>Draft</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.INVOICED: {
				cellStyle.push(styles['invoice__status-cell--invoiced']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-invoice_status_invoiced" />
						<b>Invoiced</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.PARTIALLY_PAID: {
				cellStyle.push(styles['invoice__status-cell--partially-paid']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-check" />
						<b>Partially paid</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.PAID: {
				cellStyle.push(styles['invoice__status-cell--paid']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-check" />
						<b>Paid</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.OVERPAID: {
				cellStyle.push(styles['invoice__status-cell--overpaid']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-check" />
						<b>Overpaid</b>
					</span>
				);
			}
			default: {
				return <EmptyCell />;
			}
		}
	}, []);

	const renderStatusMenuItem = React.useCallback((optionItem: InvoiceStatusFilterItem) => {
		return !!optionItem && Object.values<string>(InvoiceStatusDisplay).includes(optionItem.name)
			? renderInvoiceStatus(optionItem.name as InvoiceStatusDisplay)
			: <b>All statuses</b>;
	}, [renderInvoiceStatus]);

	const renderAdditionalFilter = React.useCallback(() => {
		const defaultFilter = InvoiceStatusFilterItem.STATUSES.find((status) => status.id === +storageStatusId) ?? InvoiceStatusFilterItem.DEFAULT_ALL;
		return (
			<>
				<div className={bemElement('table-filter', 'parameter', ['no-margin-top', 'margin-right'])}>
					<Dropdown<InvoiceStatusFilterItem>
						className={styles['invoices-table__filter-button']}
						defaultValue={defaultFilter}
						id="invoices-statuses-filter"
						labelKey="name"
						onValueChange={filterByInvoiceStatus}
						options={[InvoiceStatusFilterItem.DEFAULT_ALL, ...InvoiceStatusFilterItem.STATUSES]}
						renderMenuItem={renderStatusMenuItem}
						valueKey="id"
						withBorder={false}
					/>
					<RectangleButton
						action={undefined}
						isLeftOpen={true}
						isSquare={true}
						label={<span className="icon-filter" />}
					/>
				</div>
				<DateFilter
					areButtonsSmall={true}
					changePeriod={changePeriod}
					endDate={endDate}
					onChange={filterByDate}
					period={period}
					startDate={startDate}
				/>
			</>

		);
	}, [changePeriod, endDate, filterByDate, filterByInvoiceStatus, period, renderStatusMenuItem, startDate, storageStatusId]);

	const renderTextCell = React.useCallback(() => (cell) => {

		return <TextCell value={cell.getValue()} />;
	}, []);

	const renderDateCellFromDate = React.useCallback(() => (cell) => {

		const dbDate = cell.getValue() as Date;

		if (!dbDate) {
			return <EmptyCell />;
		}

		const date = TimeUtils.formatDate(dbDate, TimeFormat.DATE_ONLY);

		return <TextCell value={date} />;
	}, []);

	const renderDateCellFromString = React.useCallback(() => (cell) => {

		const dbDate = cell.getValue() as string;

		if (!dbDate) {
			return <EmptyCell />;
		}

		const date = TimeUtils.formatDate(dbDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);

		return <TextCell value={date} />;
	}, []);

	const renderDollarAmountCell = React.useCallback(() => (cell) => {
		if (cell.getValue() === undefined || cell.getValue() === null) {
			return null;
		}

		return (
			<DollarCell isUnitPrice={true} value={cell.getValue()} />
		);
	}, []);

	const renderPercentageCell = React.useCallback(() => (cell) => {
		if (cell.getValue() === undefined || cell.getValue() === null) {
			return null;
		}

		return (
			<PercentageCell value={cell.getValue()} />
		);
	}, []);

	const renderInvoiceStatusCell = React.useCallback(() => (_cell: CellContext<W_Accounting_FindInvoicesTable_VM_Row, unknown>) => {
		return renderInvoiceStatus(_cell.cell.row.original.status);
	}, [renderInvoiceStatus]);

	const renderAttachmentsCell = React.useCallback(() => (cell) => {
		if (!cell.getValue() || cell.getValue().length === 0) {
			return null;
		}

		const onDownloadAttachmentsClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			e.stopPropagation();
			const attachmentIds: number[] = cell.getValue();
			/** Used to identify toast to update */
			const toastId = `${attachmentIds.join('|')}-attachments`;

			const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
			loadingToast(toastId, { title: 'Downloading attachments...', timestamp: startingTimestamp });

			const fileName = `${cell.row.original.jobCode}_${cell.row.original.invoiceCode}.zip`;
			downloadAttachmentsAsZip(attachmentIds, fileName);

			successToast(toastId, { title: 'Download complete', description: fileName, timestamp: startingTimestamp });
		};

		return (
			<span className={styles['invoice__download-cell']} onClick={onDownloadAttachmentsClick.bind(this)}>
				<span className="icon-attachment" />
				<span className={styles['invoice__download-cell__text']}>
					Download
				</span>
			</span>
		);

	}, [downloadAttachmentsAsZip]);

	const renderLastReminderDateCell = React.useCallback(() => (_cell: CellContext<W_Accounting_FindInvoicesTable_VM_Row, W_Accounting_FindInvoicesTable_VM_Row['lastReminderSent']>) => {
		const lastReminderSent = _cell.getValue();

		return (!lastReminderSent || _cell.row.original.status === InvoiceStatusDisplay.DRAFT)
			? <EmptyCell />
			: TimeUtils.formatDate(lastReminderSent, TimeFormat.FULL_DATE_LONG, TimeFormat.ISO_DATETIME);
	}, []);

	const buttons = React.useMemo(() => [
		{ label: 'New Invoice', type: TableButtonType.PRIMARY, hasPermission: true, onClick: openNewInvoiceModal },
	], [openNewInvoiceModal]);

	const columns: TableProps<W_Accounting_FindInvoicesTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'jobCode',
			accessor: 'jobCode',
			header: 'Job ID',
			cell: renderTextCell(),
			size: 100,
		},
		{
			id: 'invoiceCode',
			accessor: 'invoiceCode',
			header: 'Invoice ID',
			cell: renderTextCell(),
			enableSorting: true,
			size: 100,
		},
		{
			id: 'invoicingDate',
			accessor: 'invoicingDate',
			header: 'Date Of Invoicing',
			cell: renderDateCellFromString(),
			enableSorting: true,
		},
		{
			id: 'dateCreated',
			accessor: 'dateCreated',
			header: 'Date Created',
			cell: renderDateCellFromString(),
			enableSorting: true,
		},
		{
			id: 'dueDate',
			accessor: 'dueDate',
			header: 'Due Date',
			cell: renderDateCellFromString(),
			enableSorting: true,
		},
		{
			id: 'lastReminderSent',
			accessor: 'lastReminderSent',
			header: 'Last Reminder At',
			cell: renderLastReminderDateCell(),
			enableSorting: false,
		},
		{
			id: 'status',
			accessor: 'status',
			header: 'Invoice Status',
			cell: renderInvoiceStatusCell(),
		},
		{
			id: 'paidAmount',
			accessor: 'paidAmount',
			header: 'Paid Amount ($)',
			cell: renderDollarAmountCell(),
			rightAligned: true,
			enableSorting: false,
		},
		{
			id: 'totalAmount',
			accessor: 'totalAmount',
			header: 'Total Owed ($)',
			cell: renderDollarAmountCell(),
			rightAligned: true,
			enableSorting: true,
		},
		{
			id: 'percentagePaid',
			accessor: 'percentagePaid',
			header: 'Percentage Paid (%)',
			rightAligned: true,
			cell: renderPercentageCell(),
		},
		{
			id: 'lastInstallmentDate',
			accessor: 'lastInstallmentDate',
			header: 'Date Of Last Installment',
			cell: renderDateCellFromDate(),
		},
		{
			id: 'attachments',
			accessor: 'attachmentIds',
			header: 'Attachments',
			cell: renderAttachmentsCell(),
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [
		renderTextCell,
		renderDateCellFromString,
		renderLastReminderDateCell,
		renderInvoiceStatusCell,
		renderDollarAmountCell,
		renderPercentageCell,
		renderDateCellFromDate,
		renderAttachmentsCell,
		resolveActionsButton,
	]);

	React.useEffect(() => {
		startDate && setLocalStorageStartDate(startDate);
		endDate && setLocalStorageEndDate(endDate);
		period && setLocalStoragePeriod(period);
	}, [startDate, endDate, period]);

	return (
		<>
			<Breadcrumbs items={BREADCRUMBS} />
			<TableNew
				additionalFilter={renderAdditionalFilter}
				buttons={buttons}
				columns={columns}
				fetch={fetchRows}
				hasSearchInput={true}
				offsetHeight={-30}
				onRowClick={onRowClick}
				ref={tableRef}
				selectable={true}
				tableBodyClassName={styles['work-summary-table']}
				tableName={TableNameEnum.INVOICE}
			/>
			<ConfirmationModal
				body={DELETE_INVOICE_MODAL_BODY}
				closeModal={closeDeleteModal}
				closeText="Cancel"
				confirmAction={deleteInvoiceRow()}
				confirmText="Continue"
				modalStyle="danger"
				showModal={showDeleteModal}
				title="Delete invoice"
			/>
			<ConfirmationModal
				body={getInvoiceModalBody()}
				closeModal={closeInvoiceModal}
				closeText="Cancel"
				confirmAction={invoiceInvoiceRow()}
				confirmText="Continue"
				modalStyle="info"
				showModal={showInvoiceModal}
				size="md"
				title="Invoice draft?"
			/>
			<ConfirmationModal
				body={getSendReminderModalBody()}
				closeModal={closeSendReminderModal}
				closeText="Cancel"
				confirmAction={sendManualReminder()}
				confirmText="Send"
				modalStyle="info"
				showModal={showSendReminderModal}
				size="md"
				title="Send reminder?"
			/>
			{
				showInvoiceJobSelectModal &&
				<InvoiceJobSelectModal
					closeModal={closeInvoiceJobSelectModal}
					companyName={companyName}
					orgAlias={orgAlias}
					showModal={showInvoiceJobSelectModal}
				/>
			}
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;
	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	const isAdmin = UserUtils.isAdmin(userData) || companyData.isCompanyAdmin;

	return {
		companyName: companyData.name,
		isSendingInvoiceNotificationsEnabled: companyData.isSendingInvoiceNotificationsEnabled,
		isAdmin,
	};
}

function mapDispatchToProps() {
	return {
		getInvoicesTable: accountingActions.getInvoicesTable,
		invoiceJobInvoice: jobActions.invoiceJobInvoice,
		deleteInvoice: jobActions.deleteInvoice,
		sendManualReminderForInvoice: jobActions.sendManualReminderForInvoice,
		getUserGroupAndPM: jobActions.getUserGroupAndPM,
		downloadAttachmentsAsZip: AttachmentActions.downloadAttachmentsAsZip,
		getInvoiceReminderRecipients: jobActions.getInvoiceReminderRecipients,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose(
	SettingsUtils.withSettings<SettingProps>(() => ([
		{
			key: SettingsKeys.ACCOUNTING_INVOICES_START_DATE(),
			mappedName: 'startDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.ACCOUNTING_INVOICES_END_DATE(),
			mappedName: 'endDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.ACCOUNTING_INVOICES_PERIOD(),
			mappedName: 'period',
			defaultValue: TimePeriodRecurrence.DAILY,
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.ACCOUNTING_INVOICES_STATUS(),
			mappedName: 'storageStatusId',
			defaultValue: InvoiceStatusFilterItem.DEFAULT_ALL.id,
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
	])),
	connector
);

export default enhance(Invoices);
